import { lookupConfig } from './lookupConfig';
import { applyEntityLabelText, getEffectiveFormFieldMap, resolveEntityLabels, getNestedParentLabel, DEFAULT_ENTITY_LABELS } from './entityLabels';

/** Metadata for each advanced lookup (lookupConfig key). */
export const LOOKUP_REGISTRY = [
    { lookupType: 'accountId', label: 'Account', formName: 'Accounts' },
    { lookupType: 'assAccountId', label: 'Master Account', formName: 'Accounts' },
    { lookupType: 'commodityId', label: 'Commodity', formName: 'Commodities' },
    { lookupType: 'masterContractId', label: 'Contract', formName: 'Contracts' },
    { lookupType: 'haulierId', label: 'Haulier', formName: 'Hauliers' },
    { lookupType: 'driverId', label: 'Driver', formName: 'Drivers' },
    { lookupType: 'locationId', label: 'Location', formName: 'Locations' },
    { lookupType: 'sourceLocationId', label: 'Source Location', formName: 'Locations' },
    { lookupType: 'destinationLocationId', label: 'Destination Location', formName: 'Locations' },
    { lookupType: 'shipId', label: 'Ship', formName: 'Ships' },
    { lookupType: 'siteId', label: 'Site', formName: 'Sites' },
    { lookupType: 'laAreaId', label: 'LA Area', formName: 'LaAreas' },
    { lookupType: 'vehicleId', label: 'Vehicle', formName: 'Vehicles' },
    { lookupType: 'trailerId', label: 'Trailer', formName: 'Trailers' },
    { lookupType: 'vehicleTypeId', label: 'Vehicle Type', formName: 'VehicleTypes' },
    { lookupType: 'transTypeId', label: 'Transaction Type', formName: 'TransTypes' },
    { lookupType: 'treatmentId', label: 'Treatment', formName: 'Treatments' },
    { lookupType: 'vatRateId', label: 'VAT Rate', formName: 'Commodities' },
    { lookupType: 'ewcId', label: 'EWC', formName: 'Ewcs' },
    { lookupType: 'formulationId', label: 'Formulation', formName: 'Formulations' },
];

export function getLookupMeta(lookupType) {
    return LOOKUP_REGISTRY.find((r) => r.lookupType === lookupType) ?? {
        lookupType,
        label: lookupType,
        formName: 'Accounts',
    };
}

const NESTED_PARENT_LABELS = {
    assAccount: 'Master Account',
    laArea: 'LA Area',
    site: 'Site',
    account: 'Account',
    commodity: 'Commodity',
    ship: 'Ship',
    haulier: 'Haulier',
    driver: 'Driver',
    location: 'Location',
    vehicle: 'Vehicle',
    trailer: 'Trailer',
    masterContract: 'Master Contract',
    vehicleType: 'Vehicle Type',
    transType: 'Transaction Type',
};

const LOOKUP_FIELD_LABEL_OVERRIDES = {
    iox: 'I/O',
    genAgri: 'Gen Agri',
    genPort: 'Gen Port',
    genWaste: 'Gen Waste',
    genWs: 'Gen WS',
    isCa: 'Is CA',
    erpCode: 'ERP Code',
    notInUse: 'Not In Use',
    maxWt: 'Max Weight',
    tareId: 'Tare ID',
    vatRate: 'VAT Rate',
    accInvNo: 'Account Invoice No',
    operation: 'Account Operation',
    'vehicle.vehicleReg': 'Vehicle Reg',
    'trailer.vehicleReg': 'Trailer Reg',
};

function titleCase(value) {
    if (typeof value !== 'string' || !value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function getLookupFieldLabel(formName, fieldKey, tenantSettings = null) {
    if (typeof fieldKey !== 'string') return '';

    const labels = tenantSettings != null
        ? resolveEntityLabels(tenantSettings)
        : DEFAULT_ENTITY_LABELS;
    const fieldMap = getEffectiveFormFieldMap(labels);

    const override = LOOKUP_FIELD_LABEL_OVERRIDES[fieldKey];
    if (override) return applyEntityLabelText(override, labels);

    const exact = fieldMap[formName]?.find((f) => f.key === fieldKey);
    if (exact?.label) return exact.label;

    const dotIndex = fieldKey.indexOf('.');
    if (dotIndex > 0) {
        const parent = fieldKey.slice(0, dotIndex);
        const child = fieldKey.slice(dotIndex + 1);
        const childMatch = fieldMap[formName]?.find((f) => f.key === child);
        const parentLabel = getNestedParentLabel(parent, labels)
            ?? NESTED_PARENT_LABELS[parent]
            ?? titleCase(parent);
        const childLabel = childMatch?.label ?? titleCase(child);
        return applyEntityLabelText(`${parentLabel} ${childLabel}`, labels);
    }

    return titleCase(fieldKey);
}

/** All configurable column keys for a lookup (full API field list). */
export function getLookupAvailableFields(lookupType) {
    const cfg = lookupConfig[lookupType];
    return cfg?.availableFields ?? cfg?.fields ?? [];
}

/** Default visible columns when tenant has no saved preference. */
export function getDefaultLookupFields(lookupType) {
    const cfg = lookupConfig[lookupType];
    return cfg?.fields ?? cfg?.availableFields ?? [];
}

/** Default visibility in settings: matches lookupConfig.fields subset. */
export function buildDefaultColumnVisibility(lookupType) {
    const available = getLookupAvailableFields(lookupType);
    const defaults = new Set(getDefaultLookupFields(lookupType));
    return Object.fromEntries(available.map((f) => [f, defaults.has(f)]));
}

/**
 * Merge tenant-saved visibility with defaults.
 * @returns {Record<string, boolean>}
 */
export function getColumnVisibilityForLookup(lookupType, tenantSettings) {
    const fields = getLookupAvailableFields(lookupType);
    const saved = tenantSettings?.lookupModalColumns?.[lookupType];
    if (!fields.length) return {};
    if (!saved || typeof saved !== 'object') {
        return buildDefaultColumnVisibility(lookupType);
    }
    return Object.fromEntries(fields.map((f) => [f, saved[f] !== false]));
}

/**
 * Fields to show in LookupModal for this lookup type.
 */
export function getEffectiveLookupFields(lookupType, tenantSettings) {
    const available = getLookupAvailableFields(lookupType);
    if (!available.length) return [];

    const defaults = getDefaultLookupFields(lookupType);
    const saved = tenantSettings?.lookupModalColumns?.[lookupType];
    if (!saved || typeof saved !== 'object') return defaults;

    const visible = available.filter((f) => saved[f] !== false);
    if (visible.length > 0) return visible;
    if (defaults.length > 0) return [defaults[0]];
    return [available[0]];
}

/** Build lookupModalColumns payload from per-lookup visibility maps. */
export function buildLookupModalColumnsPayload(visibilityByLookup) {
    const out = {};
    for (const [lookupType, fieldMap] of Object.entries(visibilityByLookup ?? {})) {
        if (!fieldMap || typeof fieldMap !== 'object') continue;
        out[lookupType] = { ...fieldMap };
    }
    return out;
}
